import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Shield, Star, MapPin, Languages, Calendar, MessageCircle, Flag, Ban, Clock } from "lucide-react";
import { toast } from "sonner";

const VENUES = ["Third Wave Coffee, Indiranagar", "Cubbon Park (main gate)", "Dialogues Café, Koramangala", "Lahe Lahe, Indiranagar", "Blue Tokai, Church Street"];

export default function HostProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [host, setHost] = useState(null);
  const [activity, setActivity] = useState("Coffee");
  const [venue, setVenue] = useState(VENUES[0]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("11:00");
  const [duration, setDuration] = useState(120);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");

  useEffect(() => { api.get(`/hosts/${id}`).then(({ data }) => setHost(data)); }, [id]);
  if (!host) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-terracotta border-t-transparent animate-spin" /></div>;

  const book = async () => {
    if (!date) return toast.error("Pick a date");
    setBusy(true);
    try {
      const start = new Date(`${date}T${time}:00`).toISOString();
      await api.post("/bookings", { host_id: id, activity, venue, start, duration_min: duration });
      await refresh();
      toast.success("Booking confirmed! Check your dashboard.");
      setOpen(false);
      navigate("/dashboard");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const report = async () => {
    if (!reportReason) return toast.error("Tell us what's wrong");
    await api.post("/report", { target_type: "profile", target_id: id, reason: reportReason });
    toast.success("Reported. Our team will review within policy timelines.");
    setReportReason("");
  };
  const block = async () => { await api.post(`/block/${id}`); toast.success("Blocked"); navigate("/discover"); };
  const credits = user?.time_credits_minutes || 0;
  const firstMeet = true;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand pb-28">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
        <div className="grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2">
            <img src={host.avatar_url} alt={host.name} className="rounded-3xl w-full object-cover max-h-[60vh] shadow-lg" />
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="bg-sage text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> {host.trust_score}% Trusted</span>
              {host.kyc_status === "approved" && <span className="bg-charcoal text-white text-xs font-bold px-3 py-1.5 rounded-full">KYC Verified</span>}
              {host.rating && <span className="flex items-center gap-1 text-sm font-bold text-charcoal"><Star className="w-4 h-4 fill-sun text-sun" /> {host.rating} ({host.review_count})</span>}
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black text-charcoal mt-4">{host.name}{host.age ? `, ${host.age}` : ""}</h1>
            <div className="flex gap-4 text-sm text-charcoal/60 mt-2">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {host.city}</span>
              <span className="flex items-center gap-1"><Languages className="w-4 h-4" /> {(host.languages || []).join(", ")}</span>
            </div>
            <p className="text-charcoal/80 mt-4 leading-relaxed">{host.bio}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {(host.interests || []).map((i) => <span key={i} className="text-sm font-semibold bg-white border border-border text-charcoal/70 px-3 py-1.5 rounded-full">{i}</span>)}
            </div>
            <div className="flex gap-3 mt-6">
              <Button data-testid="message-host" variant="outline" onClick={() => navigate(`/chat/${id}`)} className="rounded-full border-charcoal/20"><MessageCircle className="w-4 h-4 mr-1" /> Message</Button>
              <Dialog>
                <DialogTrigger asChild><Button variant="ghost" data-testid="report-host" className="rounded-full text-charcoal/60"><Flag className="w-4 h-4 mr-1" /> Report</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Report {host.name}</DialogTitle></DialogHeader>
                  <Input data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="What's the issue?" className="rounded-xl" />
                  <DialogFooter><Button data-testid="report-submit" onClick={report} className="rounded-full bg-signal hover:bg-signal/90 text-white">Submit report</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Button variant="ghost" data-testid="block-host" onClick={block} className="rounded-full text-charcoal/60"><Ban className="w-4 h-4 mr-1" /> Block</Button>
            </div>

            <div className="bg-white rounded-2xl border border-border p-5 mt-6">
              <h3 className="font-display text-lg font-bold text-charcoal flex items-center gap-2"><Calendar className="w-5 h-5 text-terracotta" /> Availability</h3>
              <div className="flex gap-2 mt-3">
                {(host.availability || []).map((a, i) => <span key={i} className="text-sm bg-sand px-3 py-1.5 rounded-lg text-charcoal/70">{a.day} {a.start}-{a.end}</span>)}
              </div>
            </div>

            {host.reviews?.length > 0 && (
              <div className="mt-6">
                <h3 className="font-display text-lg font-bold text-charcoal mb-3">Reviews</h3>
                <div className="space-y-3">
                  {host.reviews.slice(0, 4).map((r) => (
                    <div key={r.id} className="bg-white rounded-xl border border-border p-4">
                      <div className="flex items-center gap-2"><span className="font-semibold text-sm text-charcoal">{r.reviewer_name}</span>
                        <span className="flex items-center gap-0.5">{[...Array(r.rating)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-sun text-sun" />)}</span></div>
                      <p className="text-sm text-charcoal/70 mt-1">{r.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating book bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-border p-4 z-40">
        <div className="max-w-5xl mx-auto px-2 flex items-center justify-between gap-4">
          <div className="text-sm text-charcoal/60 hidden sm:block"><Clock className="w-4 h-4 inline mr-1" /> You have <strong className="text-charcoal">{credits} min</strong> credits</div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="book-slot-button" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white px-8 h-11 flex-1 sm:flex-none transition-transform duration-200 hover:-translate-y-0.5">Book a slot</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Book {host.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="font-semibold">Activity</Label>
                  <select data-testid="book-activity" value={activity} onChange={(e) => setActivity(e.target.value)} className="mt-1.5 w-full rounded-xl border border-border p-2.5 text-sm bg-white">
                    {["Coffee", "Walk", "Board Games", "Conversation", "Food Walk"].map(a => <option key={a}>{a}</option>)}</select></div>
                <div><Label className="font-semibold">Public venue</Label>
                  <select data-testid="book-venue" value={venue} onChange={(e) => setVenue(e.target.value)} className="mt-1.5 w-full rounded-xl border border-border p-2.5 text-sm bg-white">
                    {VENUES.map(v => <option key={v}>{v}</option>)}</select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="font-semibold">Date</Label><Input data-testid="book-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 rounded-xl" /></div>
                  <div><Label className="font-semibold">Time</Label><Input data-testid="book-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5 rounded-xl" /></div>
                </div>
                <div><Label className="font-semibold">Duration</Label>
                  <div className="flex gap-2 mt-1.5">
                    {[60, 120, 180].map((d) => (
                      <button key={d} data-testid={`book-duration-${d}`} onClick={() => setDuration(d)} disabled={firstMeet && d > 120}
                        className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors duration-200 disabled:opacity-40 ${duration === d ? "bg-terracotta text-white border-terracotta" : "bg-white text-charcoal/70 border-border"}`}>{d / 60}h</button>
                    ))}
                  </div>
                  <p className="text-xs text-charcoal/50 mt-2">First meeting with a new person is capped at 2 hours for safety.</p>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="confirm-booking" onClick={book} disabled={busy} className="w-full rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">
                  {busy ? "Booking..." : `Confirm — uses ${duration} min credits`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
